import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { hapticTick } from '../../../../../_utils/haptics'
import { CHANNEL_NAMES, DISMISS_REASONS } from '../data/planner'
import { tellPlaybook } from '../data/playbook'
import agentService from '../../../../../_services/agent.service'
import { previewSubstitute } from '../../sms-composer/smsUtils'
import Preview from '../../social/components/Preview'
import { postTheme } from '../../social/render/render'
import { MailIcon, SmsIcon, InstagramIcon, SparkIcon } from '../../social/components/icons'
import '../Plans.css'

export const KIND_LABEL = { fill: 'Fill the diary', quiet: 'Fill the diary', moment: 'Date to know', review: 'Share a review', intro: 'Meet the team', winback: 'Win back', day: 'For the day', yours: 'Your campaign', setup: 'Get set up', vouchers: 'Gift vouchers' }
// A season's plans say what kind of moment they are
const OCCASION = { season: 'Season', gift: 'Gift vouchers', occasion: 'Date to know' }
export const kindLabel = p => (p.kind === 'moment' && OCCASION[p.occasion]) || KIND_LABEL[p.kind] || 'Idea'
export const whenShort = (iso) => moment(iso).calendar(null, {
  sameDay: '[Today], h:mma', nextDay: '[Tomorrow], h:mma', nextWeek: 'ddd, h:mma', sameElse: 'ddd D MMM, h:mma', lastDay: '[Yesterday], h:mma', lastWeek: 'ddd D MMM, h:mma',
}).replace(':00', '')
export const ChannelIcon = ({ channel, size = 14 }) => (channel === 'email' ? <MailIcon size={size} /> : channel === 'sms' ? <SmsIcon size={size} /> : <InstagramIcon size={size} />)
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`

// One of Bailey's plans, as a card: what, why, when, and to whom. A plan
// from Bailey's Playbook carries its other options (`choices`): "Or" swaps
// the card to one of them, so the three choices are a tap apart.
const PlanCard = ({ plan: offered, kit, salonName, onReview, onDismiss, onAction, option = null }) => {
  const [asking, setAsking] = useState(false)
  // Opened on one option (Bailey built it in the chat), else the first
  const [choice, setChoice] = useState(option)
  // A request to the owner ("introduce me") sends from the card itself
  const [sent, setSent] = useState(null)
  const choices = offered.choices || []
  const plan = (choice && (choices.find(c => c.key === choice) || {}).plan) || offered
  useEffect(() => { tellPlaybook(offered, 'shown') }, [offered.key]) // eslint-disable-line react-hooks/exhaustive-deps
  const others = choices.length ? choices.filter(c => c.key !== (plan.chosen || choices[0].key)) : []
  const on = plan.items.filter(i => i.on)
  const posts = on.filter(i => i.post).slice(0, 2)
  const email = on.find(i => i.channel === 'email')
  const sms = on.find(i => i.channel === 'sms')
  return (
    <article className="mkp-card" aria-label={plan.title}>
      <div className="mkp-card-top">
        <span className="mkp-kind"><SparkIcon size={11} />{kindLabel(plan)}{plan.partial ? ' · Part scheduled' : ''}</span>
        <h3 className="mkp-title">{plan.title}</h3>
        <p className="mkp-why">{plan.why}</p>
        {plan.how && plan.items.length > 0 && <p className="mkp-how">{plan.how}</p>}
      </div>
      {(posts.length > 0 || email || sms) && <div className="mkp-art" aria-hidden>
        {posts.map(it => (
          <span key={it.id} className={`mkp-art-post is-${it.channel}`}>
            <Preview post={it.post} theme={postTheme(it.post, { kit })} format={it.post.format} />
          </span>
        ))}
        {email && (
          <span className="mkp-art-tile is-email">
            <MailIcon size={16} />
            <b>{String(email.email.subject || '').replace(/\{\{FirstName\}\}/g, 'Sophie').replace(/\{\{SalonName\}\}/g, salonName || 'you')}</b>
          </span>
        )}
        {sms && (
          <span className="mkp-art-tile is-sms">
            <SmsIcon size={16} />
            <b>{previewSubstitute(sms.message, salonName).split('\n')[0]}</b>
          </span>
        )}
      </div>}
      {plan.start && plan.end && plan.items.length > 0 && (
        <p className="mkp-audience">{moment(plan.start).isSame(plan.end, 'day') ? moment(plan.start).format('ddd D MMM') : `${moment(plan.start).format('ddd D MMM')} to ${moment(plan.end).format('ddd D MMM')}, ${moment(plan.end).diff(moment(plan.start), 'days') + 1} days`}</p>
      )}
      {plan.action && plan.action.request && <p className="mkp-audience">{sent === 'sent' ? 'Sent. It’s waiting for them in Bailey.' : 'I’ll send it to them in Bailey, with your name on it.'}</p>}
      {plan.action && !plan.action.request && <p className="mkp-audience">{plan.action.note || 'A journey in Marketing › Journeys. It runs for each client on their own.'}</p>}
      <ul className="mkp-steps">
        {plan.items.slice().sort((a, b) => new Date(a.at) - new Date(b.at)).slice(0, 5).map(it => (
          <li key={it.id} className={it.on ? '' : 'is-off'}>
            <span className={`mkp-step-icon is-${it.channel}`}><ChannelIcon channel={it.channel} size={13} /></span>
            <b>{CHANNEL_NAMES[it.channel]}</b>
            <span>{it.on ? whenShort(it.at) : 'Off'}</span>
          </li>
        ))}
        {plan.items.length > 5 && <li className="is-more"><span>and {plan.items.length - 5} more</span></li>}
      </ul>
      {plan.audience && plan.items.some(i => i.on && (i.channel === 'email' || i.channel === 'sms')) && (
        <p className="mkp-audience">To {String(plan.audience.label || 'your clients').toLowerCase()}{plan.audience.count ? `, about ${plural(plan.audience.count, 'person', 'people')}` : ''}</p>
      )}
      {others.length > 0 && !asking && (
        <div className="mkp-choices" role="group" aria-label="Or instead">
          <span className="mkp-choices-label">Or</span>
          {others.map(c => (
            <button key={c.key} type="button" className="mkp-chip" onClick={() => { hapticTick(); setChoice(c.key) }}>{c.label}</button>
          ))}
        </div>
      )}
      <div className="mkp-actions">
        {asking ? (
          <div className="mkp-reasons" role="group" aria-label="Why not now?">
            {DISMISS_REASONS.map(r => (
              <button key={r.id} type="button" className="mkp-chip" onClick={() => { hapticTick(); setAsking(false); tellPlaybook(plan, r.id); onDismiss(plan, r.id) }}>{r.label}</button>
            ))}
            <button type="button" className="mkp-chip is-quiet" onClick={() => setAsking(false)}>Keep it</button>
          </div>
        ) : (
          <>
            <button type="button" className="mkp-ghost" onClick={() => { hapticTick(); setAsking(true) }}>Not now</button>
            {plan.action && plan.action.request
              ? <button type="button" className="mkp-primary" disabled={!!sent} onClick={() => {
                hapticTick()
                tellPlaybook(plan, 'chosen', plan.chosen)
                setSent('sending')
                agentService.playbookRequest({ what: plan.action.request, key: plan.scenario && plan.scenario.key })
                  .then(() => setSent('sent')).catch(() => setSent(null))
              }}>{sent === 'sent' ? 'Sent' : sent ? 'Sending' : plan.action.label}</button>
              : plan.action
              ? <button type="button" className="mkp-primary" onClick={() => { hapticTick(); tellPlaybook(plan, 'chosen', plan.chosen); if (onAction) onAction(plan) }}>{plan.action.label}</button>
              : <button type="button" className="mkp-primary" onClick={() => { hapticTick(); tellPlaybook(plan, 'chosen', plan.chosen); onReview(plan) }}>Review and approve</button>}
          </>
        )}
      </div>
    </article>
  )
}

export default PlanCard
